<template>
  <div :class="['checkoutList', !isZkm && 'checkout-list-regular']">
    <form v-scroll-to-error @keydown.enter.prevent>
      <module-title
        :event="event"
        color="#333333"
        :title="$t('registerPaymentVerification')"
        :class="['module-title-card ml-0', !isZkm && 'checkout-title-regular']"
        :font-style="titleStyle"
      />
      <div :class="['container', !isZkm && 'container-regular']">
        <div v-if="user == null" ref="loginOptions" class="zkm-border">
          <div class="subtitle-checkout">{{ $t('notLoggedIn') }}</div>
          <user-login @success="refetchUser" />
        </div>
        <div v-else-if="!isZkm" class="regular-logged-in">
          <div class="btn-text btn-text-zkm">{{ $t('singedInAs') }}</div>
          <div class="user-info">
            <div class="user-name">
              <img src="../../../assets/round-person.svg">
              <span class="name-container"> {{ user.firstName }} {{ user.lastName }} </span>
            </div>
            <div class="identity-container">
              <span
                class="identity-question-container"
              >{{ $t('youAreNot') }} {{ user.firstName }}?</span>
              <a id="notYourProfile" style="color: inherit" @click="onLogout">
                <span class="logout-link-container">{{ $t('clickHere') }}</span>
              </a>
            </div>
          </div>
        </div>
        <tickets-insurance-container
          v-if="!isTotalPriceZero"
          v-model="form.insurance"
          :validation="$v"
          :ticket-insurance-amount="ticketInsuranceAmount"
        />
        <payment-method
          v-if="!isTotalPriceZero"
          :tickets="attendeeTicket"
          :event="event"
          @paymentMethod="paymentMethod($event)"
        />
        <div class="title-description mb-32 pl-0">
          {{ $t('ticketPersonalizationCheckoutDescription') }}
        </div>
        <div v-if="user !== null && isZkm">
          <div class="profile-title">{{ $t('yourAccount') }}</div>
          <p>{{ $t('singedInAs') }}</p>
          <button class="profile-btn">
            <img class="profile-icon" src="../../../assets/account-icon.svg">
            <div class="btn-text">{{ user.firstName }} {{ user.lastName }}</div>
          </button>
          <div class="not-profile-container">
            <p>{{ $t('youAreNot') }} {{ user.firstName }}?</p>
            <a id="notYourProfile" class="not-profile" @click="onLogout">
              <p>{{ $t('clickHere') }}</p>
            </a>
          </div>
        </div>
        <div v-show="user">
          <div
            v-if="!hideBuyerFields"
            :class="[
              'customer-data-title',
              isZkm ? 'subtitle-store-checkout-zkm' : 'subtitle-store-checkout-regular',
            ]"
          >
            {{ $t('customerData') }}
          </div>
          <p class="customer-data-description">
            {{ $t('customerDataDescription') }}
          </p>
          <checkout-form
            ref="checkoutFormRef"
            :event="event"
            :styles="styles.form"
            :fields="buyerAttendeeFields"
            :category="category"
            :user="user"
            :initial-values="initialValues"
            :vs="$v"
            :is-zkm="isZkm"
            :is-gift-card-selected="isGiftCardSelected"
            :total-order-price="totalOrderPrice"
            @values="updateBuyerFormValues"
            @applyGiftCard="applyGiftCard"
          >
            <template v-slot:after-email>
              <div v-if="accessibleAttendeeIndices.length" class="disability-card-fields">
                <text-field
                  v-for="(attendeeIndex, i) in accessibleAttendeeIndices"
                  :key="'disability-card-' + attendeeIndex"
                  v-model.trim="attendeeTicket[attendeeIndex].__disabilityCardNumber"
                  :label="
                    $t('disabilityCardNumber') +
                      (accessibleAttendeeIndices.length > 1 ? ' ' + (i + 1) : '') +
                      '*'
                  "
                  type="text"
                />
              </div>
            </template>
            <template v-slot:before-terms>
              <ticket-owners-accordion
                v-if="hasTicketPersonalization && !hideBuyerFields"
                v-model="form.ticketOwners"
                :tickets="selectedTickets"
                :buyer="buyerOwnerFields"
                :fields="ticketOwnerAttendeeFields"
                :validation="$v.form.ticketOwners"
                :validation-attempt="ticketOwnersValidationAttempt"
              />
            </template>
          </checkout-form>
        </div>
        <div class="attendee-actions attendee-modals-zkm" :style="{ display: !isZkm && 'none' }">
          <main-button
            class="back-btn"
            flat
            :text="styles.goBack || $t('back')"
            color="#333"
            @click="$emit('back')"
          />

          <main-button
            v-if="event.extraModules.workshops"
            id="continueWorkshop"
            class="continue-btn"
            depressed
            :text="styles.continue || 'Nastavi na radionice'"
            color="#333"
            :disabled="disabledSubmitButton"
            @click="submitForm()"
          />
          <main-button
            v-else
            id="confirmOrder"
            class="continue-btn"
            depressed
            :text="styles.continue || $t('confirmOrder')"
            color="#333"
            :disabled="disabledSubmitButton"
            @click="submitForm()"
          />
        </div>
      </div>
    </form>
    <simple-snackbar v-bind.sync="snackbar" />
  </div>
</template>

<script>
import ModuleTitle from './../../shared/module-title.vue'
import CheckoutForm from './../checkout-form.vue'
import { submitGuard } from '@/utils'
import PaymentMethod from './payment-method.vue'
import TicketOwnersAccordion from './ticket-owners-accordion.vue'

import {
  decimal,
  helpers,
  maxLength,
  maxValue,
  minLength,
  minValue,
  required,
  requiredIf,
  sameAs,
  email,
} from 'vuelidate/lib/validators'
import UserLogin from './UserLogin.vue'
import { logout } from '../../../http'
import SimpleSnackbar from '../../shared/snackbars/simple-snackbar.vue'
import { mapActions, mapGetters } from 'vuex'
import TicketsInsuranceContainer from './tickets-insurance-container.vue'
import { hasVatNumberSeparators, isValidVatNumberForCountry } from '@/utils/vies'

const isSuccessfullyAppliedGiftCard = isSuccessful =>
  helpers.withParams(
    { type: 'isSuccessfullyAppliedGiftCard', value: isSuccessful },
    value => !value || value.length !== 20 || isSuccessful,
  )

const BUYER_FIELD_KEYS_WITH_PERSONALIZATION = [
  'First name',
  'Last name',
  'Email',
  'Address',
  'City',
  'Postal code',
  'Country',
]
const TICKET_OWNER_META_KEYS = [
  'ticketId',
  'ticketName',
  'firstName',
  'lastName',
  'email',
  'sameAsBuyer',
  'sendTicketToOwner',
  'sendTicketToOwnerTouched',
]

export default {
  name: 'checkout',
  components: {
    CheckoutForm,
    ModuleTitle,
    UserLogin,
    PaymentMethod,
    SimpleSnackbar,
    TicketsInsuranceContainer,
    TicketOwnersAccordion,
  },

  props: {
    event: {
      type: Object,
      default: null,
    },
    category: {
      type: Object,
      default: null,
    },
    styles: {
      type: Object,
      default: () => ({}),
    },
    schema: {
      type: Object,
      default: () => ({}),
    },
    initialValues: {
      type: Object,
      default: () => ({}),
    },
    selectedTickets: {
      type: Array,
      default: () => [],
    },
    totalOrderPrice: {
      type: String,
      default: null,
    },
    hideBuyerFields: {
      type: Boolean,
      default: false,
    },
    ticketInsuranceAmount: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      snackbar: {
        open: false,
        messageProp: '',
        color: 'primary',
      },
      form: this.initialValues,
      attendeeTicket: this.initialValues.tickets
        ? this.initialValues.tickets
        : this.selectedTickets.reduce((acc, val) => {
          let values = acc
          values.push({ ticketId: val.id })

          return values
        }, []),
      secondaryColor: '#0277BD',
      paymentType: null,
      ticketOwnersValidationAttempt: 0,
      colorZkm: '#333333',
      titleStyle: {
        fontFamily: 'Raleway',
        fontSize: '2.25rem',
        fontStyle: 'normal',
        fontWeight: '800',
        lineHeight: '120%',
        letterSpacing: '-0.0675rem',
      },
      isGiftCardSelected: false,
      giftCard: null,
      codeClient: null,
      identityRetryTimer: null,
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/user',
    }),
    accessibleAttendeeIndices() {
      return this.attendeeTicket.reduce((indices, entry, index) => {
        if (this.isAccessibleAttendee(entry)) {
          indices.push(index)
        }

        return indices
      }, [])
    },
    disabledSubmitButton() {
      if (!this.isTotalPriceZero && !this.paymentType) return true
      if (this.isZkm) {
        if (!this.$store.getters['auth/isAuthenticated']) return true
        if (this.hideBuyerFields) return false

        return this.$v.$invalid
      } else {
        if (!this.$store.getters['auth/isAuthenticated']) return true
        if (this.hideBuyerFields) return false

        return this.$v.$invalid
      }
    },
    isTotalPriceZero() {
      if (this.giftCard) {
        return (
          Number(this.totalOrderPrice.split(' ')[1]) - Number(this.giftCard.amount) <= 0 &&
          this.giftCard.isInRange
        )
      }

      return Number(this.totalOrderPrice.split(' ')[1]) <= 0
    },
    isZkm() {
      return this.event.organization.id == process.env.VUE_APP_ZKM_ORGANIZATION_ID
    },
    hasTicketPersonalization() {
      return !!(
        this.event &&
        this.event.extraModules &&
        this.event.extraModules.ticketPersonalization
      )
    },
    buyerOwnerFields() {
      return {
        firstName: this.form['First name'] || this.form.firstName || '',
        lastName: this.form['Last name'] || this.form.lastName || '',
        email: this.form.Email || this.form.email || '',
      }
    },
    buyerAttendeeFields() {
      if (!this.hasTicketPersonalization) {
        return this.schema.attendeeFields || []
      }

      return (this.schema.attendeeFields || []).filter(field =>
        BUYER_FIELD_KEYS_WITH_PERSONALIZATION.includes(field.key),
      )
    },
    ticketOwnerAttendeeFields() {
      if (!this.hasTicketPersonalization) {
        return []
      }

      return (this.schema.attendeeFields || []).filter(
        field => !BUYER_FIELD_KEYS_WITH_PERSONALIZATION.includes(field.key),
      )
    },
    buyerValidationSchema() {
      if (!this.hasTicketPersonalization) {
        return this.schema.validationSchema || {}
      }

      return BUYER_FIELD_KEYS_WITH_PERSONALIZATION.reduce((acc, key) => {
        if (this.schema.validationSchema && this.schema.validationSchema[key]) {
          acc[key] = this.schema.validationSchema[key]
        }

        return acc
      }, {})
    },
  },
  watch: {
    initialValues: {
      handler() {
        this.form = {
          ...this.form,
          ...this.initialValues,
        }
      },
    },
    form: {
      handler(value) {
        if (this.hasTicketPersonalization && value && !value.ticketOwners) {
          this.$set(this.form, 'ticketOwners', [])
        }
      },
      immediate: true,
      deep: true,
    },
    'form.paymentType': function (val) {
      if (val === 'GiftCard') {
        this.isGiftCardSelected = true
        if (!this.isGiftCardSelected && !!this.$refs.checkoutFormRef.$data.form.giftCardCode) {
          this.$refs.checkoutFormRef.$data.isSuccessfullyAppliedGiftCard = false
          this.$refs.checkoutFormRef.$data.form.giftCardCode = ''
          this.$refs.checkoutFormRef.$data.form.giftCardAmount = ''
        }
      }
    },
    'form.insurance.selected': {
      handler(value) {
        this.$emit('changeInsurance', value)
      },
    },
    selectedTickets: {
      handler() {
        this.attendeeTicket = this.initializeAttendeeTicket()
      },
    },
    // the card number belongs to one specific attendee, so it lives on that attendee's entry
    accessibleAttendeeIndices: {
      immediate: true,
      handler(indices) {
        indices.forEach(attendeeIndex => {
          const attendee = this.attendeeTicket[attendeeIndex]
          if (attendee && attendee.__disabilityCardNumber === undefined) {
            this.$set(attendee, '__disabilityCardNumber', '')
          }
        })
      },
    },
    user: {
      handler(value) {
        this.$emit('user', value)
      },
    },
  },
  beforeDestroy() {
    if (this.identityRetryTimer) {
      clearTimeout(this.identityRetryTimer)
      this.identityRetryTimer = null
    }
  },
  validations() {
    //remove from validation for older events - remove this when events are over
    delete this.schema.validationSchema['Document Id']
    delete this.schema.validationSchema.Function

    const ticketOwnersValidation = {}
    if (this.hasTicketPersonalization) {
      const attendeeFieldValidation = this.ticketOwnerAttendeeFields.reduce((acc, field) => {
        acc[field.key] =
          this.schema.validationSchema && this.schema.validationSchema[field.key]
            ? this.schema.validationSchema[field.key]
            : {}

        return acc
      }, {})

      ticketOwnersValidation.ticketOwners = {
        $each: {
          firstName: { required },
          lastName: { required },
          email: { required, email },
          ...attendeeFieldValidation,
        },
      }
    }

    return {
      form: {
        ...this.buyerValidationSchema,
        acceptTermsAndConditions: {
          sameAs: sameAs(() => true),
        },
        legalEntityName: { required: requiredIf(() => this.form.r1Invoice) },
        legalEntityOIB: {
          required: requiredIf(() => this.form.r1Invoice),
          validVatNumberFormat: value =>
            !this.form.r1Invoice || !helpers.req(value) || !hasVatNumberSeparators(value),
          validVatNumber: value =>
            !this.form.r1Invoice || !helpers.req(value) || isValidVatNumberForCountry(value, this.form.legalEntityCountry),
        },
        legalEntityEmail: { required: requiredIf(() => this.form.r1Invoice), email },
        legalEntityAddress: { required: requiredIf(() => this.form.r1Invoice) },
        legalEntityZipcode: { required: requiredIf(() => this.form.r1Invoice) },
        legalEntityCity: { required: requiredIf(() => this.form.r1Invoice) },
        legalEntityCountry: { required: requiredIf(() => this.form.r1Invoice) },
        ...ticketOwnersValidation,
        giftCardCode: {
          required: requiredIf(() => this.isGiftCardSelected),
          minLength: minLength(10),
          maxLength: maxLength(20),
          isSuccessfullyApplied: isSuccessfullyAppliedGiftCard(
            this.form.giftCardCode
              ? this.$refs.checkoutFormRef.$data.isSuccessfullyAppliedGiftCard
              : '',
          ),
        },
        'OIB / LIB (National Identification Number)':
          Number(this.event.id) === 2953
            ? {
              required,
              minLength: minLength(11),
              maxLength: maxLength(11),
            }
            : {},
        giftCardAmount:
          this.$refs.checkoutFormRef && this.$refs.checkoutFormRef.$data.maxGiftCardAmount
            ? {
              required: requiredIf(() => this.isGiftCardSelected),
              decimal,
              minValue: minValue(0.01),
              maxValue: maxValue(this.$refs.checkoutFormRef.$data.maxGiftCardAmount),
            }
            : {
              required: requiredIf(() => this.isGiftCardSelected),
              decimal,
              minValue: minValue(0.01),
            },
        ...(this.isTotalPriceZero
          ? {}
          : {
            insurance: {
              selected: { required: requiredIf(() => !this.isTotalPriceZero) },
              isTermsAccepted: {
                acceptedIfInsuranceSelected: val =>
                  this.form.insurance.selected !== true || val === true,
              },
            },
          }),
      },
      attendeeTicket: {
        $each: {
          __disabilityCardNumber: {
            requiredForAccessibleTicket: (value, attendee) =>
              !this.isAccessibleAttendee(attendee) || !!(value && String(value).trim()),
          },
        },
      },
    }
  },
  methods: {
    ...mapActions({
      vuexLogout: 'auth/logout',
      fetchUser: 'auth/fetchUser',
    }),
    paymentMethod(method) {
      if (method === 'GiftCard') {
        this.isGiftCardSelected = !this.isGiftCardSelected
        if (!this.isGiftCardSelected && !!this.$refs.checkoutFormRef.$data.form.giftCardCode) {
          this.$refs.checkoutFormRef.$data.isSuccessfullyAppliedGiftCard = false
          this.$refs.checkoutFormRef.$data.form.giftCardCode = ''
          this.$refs.checkoutFormRef.$data.form.giftCardAmount = ''
        }

        return
      }
      this.paymentType = method
    },
    triggerButtonClick() {
      this.submitForm()
    },
    scrollToLoginOptions() {
      this.$nextTick(() => {
        const loginOptions = this.$refs.loginOptions

        if (!loginOptions) {
          return
        }

        const checkoutTimer = document.querySelector(
          '.checkout-timer-regular-mobile, .checkout-timer-regular-mobile-no-cover',
        )
        const timerOffset =
          checkoutTimer && window.getComputedStyle(checkoutTimer).position === 'fixed'
            ? checkoutTimer.getBoundingClientRect().height + 16
            : 16
        const top = loginOptions.getBoundingClientRect().top + window.pageYOffset - timerOffset

        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' })
      })
    },
    async onLogout() {
      await logout()
      await this.vuexLogout()
    },
    isAccessibleAttendee(attendee) {
      const ticket =
        attendee && this.selectedTickets.find(t => String(t.id) === String(attendee.ticketId))

      return !!(ticket && ticket.itemMeta && ticket.itemMeta.isAccessible)
    },
    changeValue(index, event) {
      const { ticketId, __disabilityCardNumber } = this.attendeeTicket[index]
      this.attendeeTicket.splice(index, 1, { ...event, ticketId, __disabilityCardNumber })
    },
    updateBuyerFormValues(values) {
      const buyerValues = { ...values }
      delete buyerValues.ticketOwners

      this.form = { ...this.form, ...buyerValues }
    },
    async submitForm() {
      if (!this.isTotalPriceZero && !this.paymentType) return
      await submitGuard(
        this,
        async () => {
          this.$emit('next', this.connectAttendeeTickets())
        },
        () => {
          this.handleInvalidSubmit()
          this.scrollToFirstError()
        },
      )
    },
    scrollToFirstError() {
      // wait for the re-render so the touched fields (and an expanded ticket owner panel) show their error state
      this.$nextTick(() => {
        const firstError = Array.from(
          this.$el.querySelectorAll('.error--text, .insurance-error-text'),
        ).find(el => el.getClientRects().length)

        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    handleInvalidSubmit() {
      if (this.$v.attendeeTicket && this.$v.attendeeTicket.$invalid) {
        this.snackbar = {
          open: true,
          messageProp: this.$t('disabilityCardNumberRequired'),
          color: 'error',
        }

        return
      }
      if (
        this.hasTicketPersonalization &&
        this.$v.form.ticketOwners &&
        this.$v.form.ticketOwners.$invalid
      ) {
        this.ticketOwnersValidationAttempt += 1
        this.snackbar = {
          open: true,
          messageProp: this.$t('ticketOwnerValidationError'),
          color: 'error',
        }

        return
      }

      console.warn('Form not valid')
    },

    connectAttendeeTickets() {
      let merged = []

      for (let [index, value] of this.attendeeTicket.entries()) {
        const owner =
          this.hasTicketPersonalization && this.form.ticketOwners && this.form.ticketOwners[index]
        let acc = { ...value }
        if (owner) {
          acc = {
            ...acc,
            ...this.getTicketOwnerAttendeeFields(owner),
            firstName: owner.firstName,
            lastName: owner.lastName,
            email: owner.email,
            sameAsBuyer: owner.sameAsBuyer,
            sendTicketToOwner: owner.sendTicketToOwner,
          }
        }
        merged.push(acc)
      }

      const formFields = { ...this.form }
      delete formFields.ticketOwners

      return { tickets: merged, ...formFields, paymentType: this.paymentType }
    },
    getTicketOwnerAttendeeFields(ticketOwner) {
      if (!this.hasTicketPersonalization || !ticketOwner) {
        return {}
      }

      return Object.entries(ticketOwner).reduce((acc, [key, value]) => {
        if (
          !TICKET_OWNER_META_KEYS.includes(key) &&
          !BUYER_FIELD_KEYS_WITH_PERSONALIZATION.includes(key)
        ) {
          acc[key] = value
        }

        return acc
      }, {})
    },
    // one entry per selected ticket, in cart order - the order the created purchases follow, so
    // per-attendee data (owner fields, disability card number) stays on the right ticket
    initializeAttendeeTicket() {
      const reusable = (this.attendeeTicket && this.attendeeTicket.length
        ? this.attendeeTicket
        : this.initialValues.tickets || []
      ).filter(Boolean)

      return this.selectedTickets.map(ticket => {
        const index = reusable.findIndex(
          entry => String(entry.ticketId) === String(ticket.id),
        )

        return index === -1 ? { ticketId: ticket.id } : reusable.splice(index, 1)[0]
      })
    },
    async refetchUser() {
      await this.fetchUser()
    },
    applyGiftCard(giftCardData) {
      this.$emit('applyGiftCard', giftCardData)
    },
  },
}
</script>

<style scoped>
.disability-card-fields {
  margin-top: 8px;
}

.signin-buttons {
  display: flex;
  align-items: flex-start;
  flex-direction: column-reverse;
  gap: 24px;
  @media screen and (max-width: 520px) {
    flex-direction: column;
    gap: 0;
    align-items: center;
  }
}

.zkm-hide {
  display: none;
}

.attendee-actions-zkm {
  background: var(--grey-40, #f5f5f5);
  margin: -24px;
  padding: 24px;
  @media screen and (max-width: 800px) {
    margin: 0;
  }
}

.zkm-border {
  border: solid var(--stroke-one, rgba(151, 151, 151, 0.5));
  border-width: 0 0 1px 0;
  border-radius: 0px;
  margin: 0px -24px;
  padding: 2rem 1.5rem;
  padding-top: 0;
}

.zkm-border .subtitle-checkout {
  margin-top: 1rem;
  text-align: left;
  padding-top: 2rem;
}

.container {
  border: 1px solid var(--stroke-one, rgba(151, 151, 151, 0.5));
  padding: 24px;
  padding-top: 0;
  border-radius: 6px;
}

.container-regular {
  @media screen and (max-width: 992px) {
    border-left: unset;
    border-right: unset;
    border-radius: unset;
  }
  @media screen and (max-width: 992px) and (min-width: 960px) {
    width: 100%;
  }
  @media screen and (max-width: 992px) and (min-width: 801px) {
    margin-top: 5rem;
  }
}

.module-title-card {
  margin-bottom: 2.5rem;
  @media screen and (max-width: 1000px) {
    margin-bottom: 2rem;
  }
}

.back-button-container {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  cursor: pointer;
  width: fit-content;
}

.back-button-icon-container {
  margin-right: 1rem;
  display: flex;
  align-items: center;
}

.back-button-icon {
  height: 1rem;
}

.back-button-text-container {
  font-family: 'Raleway', sans-serif;
  font-size: 1.25rem;
  font-weight: bold;
  color: #ff4c00;
  line-height: 120%;
  letter-spacing: -0.015em;
}

.subtitle-checkout {
  color: #333;
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%; /* 24px */
  letter-spacing: -0.6px;
}

.subtitle-checkout-zkm {
  margin-bottom: 3rem;
}

button {
  background: white;
  border: 2px solid #0277bd;
  border-radius: 24px;
  padding: 8px 60px;
  margin-top: 24px;
}

button div {
  color: #0277bd;
  font-weight: bold;
  font-size: 16px;
  font-family: Raleway, 'sans-serif';
  line-height: 100%;
}

p {
  font-family: 'Source sans pro';
  font-size: 16px;
  line-height: 20px;
  color: #666666;
}

.terms-checkbox {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.profile-title {
  font-size: 20px;
  font-weight: 600;
  font-family: Raleway, 'sans-serif';
  margin-bottom: 16px;
}

.profile-btn {
  display: flex;
  border: none;
  background-color: #e6f1f8;
  color: #0277bd;
  border-radius: 24px;
  padding: 12px 14px;
  font-size: 20px;
  font-weight: 700;
  justify-content: space-around;
  align-items: center;
  margin: 16px 0;
  cursor: unset;
}

.attendee-actions {
  display: flex;
  justify-content: flex-end;
}

@media screen and (max-width: 800px) {
  .attendee-actions {
    flex-direction: column;
    max-width: 100%;
  }
}

.email-signin-btn {
  text-transform: uppercase;
  height: 44px;
}

.email-signin-btn-zkm {
  border-color: #333333;
  background: var(--grey-40, #f5f5f5);
  padding: 0.75rem 1.5rem;
}

.btn-text-zkm {
  font-size: 18px;
  color: #333333;
}

.btn-text {
  margin-right: 4px;
  margin-left: 10px;
}

.not-profile-container {
  display: flex;
}

.not-profile {
  margin-left: 4px;
}

@media (max-width: 992px) and (min-width: 801px) {
  .checkoutList {
    padding: 0 2rem;
  }
}

.back-btn {
  border: none !important;
}

.not-profile p {
  color: #0277bd;
  text-decoration: underline;
}

@media screen and (max-width: 800px) {
  .attendee-actions {
    flex-direction: column;
    max-width: 100%;
  }
}

@media screen and (max-width: 450px) {
  .regular-logged-in {
    margin: 1rem -24px 0px !important;
  }
}

@media (max-width: 992px) and (min-width: 801px) {
  .checkoutList {
    padding: 0 2rem;
  }
}

.back-btn {
  border: none !important;
}

.regular-logged-in {
  display: flex;
  margin: 0rem -24px;
  padding: 2rem 1.5rem;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 1.5rem;
  align-self: stretch;
  border-bottom: 1px solid rgba(151, 151, 151, 0.5);
  color: #333;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.0375rem;
  @media screen and (min-width: 801px) and (max-width: 992px) {
    margin-top: -0.2rem;
  }
}

.user-info {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 1rem;
  flex-wrap: wrap;
}

.user-name {
  display: flex;
  height: 2.875rem;
  padding: 0.75rem 1.5rem 0.75rem 1.25rem;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 1.5rem;
  border: 2px solid #333;
}

.name-container {
  font-variant-numeric: unset;
  font-feature-settings: 'clig' off, 'liga' off;
  font-weight: 700;
  letter-spacing: 0.01688rem;
}

.identity-container {
  display: flex;
  gap: 0.38rem;
}

.identity-question-container {
  font-variant-numeric: unset;
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.015rem;
}

.logout-link-container {
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.015rem;
  border-bottom: 0.5px solid #333;
}

.subtitle-store-checkout-regular {
  margin: 0 -1.5rem;
  padding: 2rem 1.5rem 0;
}

.checkout-title-regular {
  @media screen and (max-width: 992px) {
    display: none;
  }
}

.checkout-list-regular {
  @media screen and (max-width: 992px) and (min-width: 801px) {
    margin: -3.5rem -3.5rem 0 -3.5rem;
  }
  @media screen and (max-width: 800px) {
    margin: 0 -1.5rem 0 -1.5rem;
  }
}

.here-link {
  text-decoration: underline;
  cursor: pointer;
}

.external-browser-text {
  margin: 0;
  color: #666;
  text-align: center;
  font-family: 'Source Sans Pro';
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.customer-data-title {
  color: var(--Grey-00, #333);
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.72px;
}

.customer-data-description {
  color: #333;
  margin: 0.5rem 0 1.5rem 0;
  font-family: 'Source Sans Pro';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}
</style>
